import TWEEN from '@tweenjs/tween.js';

/**
 * @module Animations
 *
 * @description Provides functions to animate visual objects
 *
 * Since the animations are created using tween.js, the Scene animation loop will need to call TWEEN.update() to update the animations on each frame.
 *
 *
 */

/**
 * @function pulsing
 *
 * @description Pulsing animation for visual objects.
 *
 * Default values specified as per @link [ticket](https://gitlab.com/ObsessVR/360Experiences/development-tasks/-/issues/6192)
 *
 * @static
 * @description Pulsing animation for visual objects
 * @param {THREE.Object3D} visualObject - The visual object to apply animation to
 * @param {Object} animationProps - Animation Properties
 * @param {Number} [animationProps.hotspot_pulsing_frequency=1] - Frequency of pulsing animation
 * @param {Number} [animationProps.hotspot_pulsing_scale=1.2] - Scale of pulsing animation
 * @param {Number} [animationProps.hotspot_pulsing_delay=300] - Delay of pulsing animation
 * @return {Tween<*>}
 *
 *
 * @example
 * import { pulsing } from 'animations';
 *
 * const visualObject = new THREE.Mesh(geometry, material);
 * const animationProps = {
 * 		hotspot_pulsing_frequency: 1,
 * 		hotspot_pulsing_scale: 1.2,
 * };
 * pulsing(visualObject, animationProps);
 */
const createPulsingAnimation = (visualObject, animationProps) => {
	const {
		hotspot_pulsing_frequency,
		hotspot_pulsing_scale = 1.2,
		hotspot_pulsing_delay = 300,
	} = animationProps;
	const initialScale = visualObject.scale.clone();

	const _tween = new TWEEN.Tween(visualObject.scale)
		.to(
			{
				x: visualObject.scale.x * hotspot_pulsing_scale,
				y: visualObject.scale.y * hotspot_pulsing_scale,
				z: visualObject.scale.z * hotspot_pulsing_scale,
			},
			1000 / hotspot_pulsing_frequency,
		)
		.easing(TWEEN.Easing.Sinusoidal.Out)
		.repeat(Infinity)
		.onRepeat(async (x) => {
			/** Trigger a wait if the _tween._repeatCount is even */
			if (_tween._repeatCount % 2 === 0) {
				_tween.pause();
				setTimeout(() => {
					_tween.resume();
				}, hotspot_pulsing_delay);
			}
			_tween._repeatCount += 1;
		})
		.onStop(() => {
			/* Reset to initial scale on stop */
			visualObject.scale.set(
				initialScale.x,
				initialScale.y,
				initialScale.z,
			);
		})
		.yoyo(true);

	/**
	 * @private
	 * _tween.repeatCount is an aux variable added to the tween object to track the repeat index.
	 * We use repeatIndex to check if the repeat index is even (i.e. when the tween is set to
	 * initial scale of the object) to add an explicit delay using setTimeout. This is introduced
	 * due to limitation of the tween.js library to add a delay at this position.
	 *
	 * We start with an index of 1 and then increase the index in onRepeat callback of the animation and
	 * only trigger a pause if the index is even (index % 2 === 0).
	 *
	 *  */
	_tween._repeatCount = 1;
	return _tween;
};

/**
 *
 * @function animate
 * @name createAnimationByType
 * @static
 *
 * @description A wrapper function that Applies animation to a visual object based on animationProps#type
 *
 * @param {THREE.Object3D} visualObject  - The visual object to apply animation to
 * @param {Object} animationProps - The animation properties, see supported animation function props for supported values that go here.
 * @param {String} animationProps.type - The type of animation to apply. Currently only 'pulsing' is supported
 *
 * @example
 * import { animate } from 'animations';
 *
 * const visualObject = new THREE.Mesh(geometry, material);
 * const animationProps = {
 * 		type: 'pulsing',
 * 		hotspot_pulsing_frequency: 1,
 * 	};
 * animate(visualObject, animationProps);
 *
 *
 * @return {Tween<*>}
 */
const createAnimationByType = (visualObject, { type, ...animationProps }) => {
	if (type === 'pulsing')
		return createPulsingAnimation(visualObject, animationProps);
	throw new Error(`No animation function for type ${type}`);
};

export { createAnimationByType, createPulsingAnimation };
